import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { Alert } from 'components/Alert';
import { Button } from 'components/Button';
import { Input } from 'components/Input';
import { Snackbar } from 'components/Snackbar';
import { useAuth } from 'contexts/AuthContext';
import { authStrings } from 'constants/strings';
import { branding } from 'constants/branding';
import { routes } from 'constants/routes';

type LoginLocationState = {
  /** RequireAuth が保存するログイン前の遷移先 */
  from?: string;
  /** ACCOUNT_PAUSED による強制サインアウトで true */
  paused?: boolean;
  /** 利用規約の同意保存後のログアウトなど、遷移元からの案内 */
  snackbarMessage?: string;
} | null;

export default function Login() {
  const { user, loading, signIn } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = (location.state ?? null) as LoginLocationState;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string | undefined>();

  // 案内は一度だけ表示する（戻り先・停止の表示は残す）
  useEffect(() => {
    const state = (location.state ?? null) as LoginLocationState;
    if (!state?.snackbarMessage) return;
    setSnackbarMessage(state.snackbarMessage);
    navigate(location.pathname, {
      replace: true,
      state: { from: state.from, paused: state.paused },
    });
  }, [location.pathname, location.state, navigate]);

  const passwordAutoComplete = 'current-password';

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;
    setError(undefined);
    setIsSubmitting(true);

    const errorMessage = await signIn(email, password);
    if (errorMessage) {
      setError(errorMessage);
    }
    setIsSubmitting(false);
  };

  // ログイン処理（同意状態の確認）が終わるまでは遷移しない。途中で遷移すると失敗を表示できない
  if (user && !loading && !isSubmitting) {
    // 組織スコープ付きの戻り先、または入口ルーティング（/）へ
    return <Navigate to={locationState?.from ?? routes.HOME} replace />;
  }

  return (
    <main className="relative min-h-screen overflow-hidden bg-surface text-ink">
      <div className="relative grid min-h-screen place-items-center px-5 py-12 sm:px-6">
        <section className="w-full max-w-[440px] rounded-[32px] border border-white/70 bg-white/90 p-6 shadow-soft backdrop-blur-sm motion-safe:animate-float-in sm:p-8">
          <div className="grid gap-6">
            <header className="flex flex-col items-center text-center">
              <img
                src={branding.logoUrl}
                alt={branding.serviceName}
                className="h-auto w-full max-w-[220px]"
              />
            </header>

            {loading ? (
              <div className="grid min-h-[220px] place-items-center gap-3 rounded-2xl border border-dashed border-border/70 bg-surface/70 text-muted">
                <span
                  className="h-9 w-9 rounded-full border-2 border-accent/20 border-t-accent motion-safe:animate-spin"
                  aria-hidden="true"
                />
                <p className="text-sm">{authStrings.checkingSessionBody}</p>
              </div>
            ) : (
              <div className="grid gap-6">
                {locationState?.paused && (
                  <Alert>{authStrings.accountPaused}</Alert>
                )}
                <form className="grid gap-4" onSubmit={handleSubmit}>
                  <label className="grid gap-2 text-sm text-ink">
                    <span className="text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-muted">
                      {authStrings.emailLabel}
                    </span>
                    <Input
                      type="email"
                      name="email"
                      autoComplete="email"
                      placeholder={authStrings.emailPlaceholder}
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      required
                    />
                  </label>
                  <label className="grid gap-2 text-sm text-ink">
                    <span className="text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-muted">
                      {authStrings.passwordLabel}
                    </span>
                    <Input
                      type="password"
                      name="password"
                      autoComplete={passwordAutoComplete}
                      placeholder={authStrings.passwordPlaceholder}
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      required
                    />
                  </label>
                  {error && <Alert>{error}</Alert>}
                  <Button type="submit" disabled={isSubmitting} fullWidth>
                    {isSubmitting ? authStrings.working : authStrings.signIn}
                  </Button>
                </form>
              </div>
            )}
          </div>
        </section>
      </div>
      <Snackbar
        open={Boolean(snackbarMessage)}
        message={snackbarMessage ?? ''}
        variant="success"
        position="top-center"
        autoHideDuration={6000}
        onClose={() => setSnackbarMessage(undefined)}
      />
    </main>
  );
}
